import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { dashboardRhService } from '../services/dashboard-rh.service';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Share2, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardIndicators } from '../components/DashboardIndicators';
import { DashboardCharts } from '../components/DashboardCharts';
import { ShareModal } from '../components/ShareModal';
import { ExportModal } from '../components/ExportModal';
import { useFolderDetail } from '@/hooks/useDomains';
import { useDashboardCalculations } from '../hooks/useDashboardCalculations';

export function DashboardPage() {
  const { t } = useTranslation();
  const { folderUuid, dashboardUuid } = useParams();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { isLoading: isLoadingFolder } =
    useFolderDetail(folderUuid);

  const [selectedEstablishment, setSelectedEstablishment] = useState<string | null>(null);

  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    error: dashboardError,
  } = useQuery({
    queryKey: ['dashboard-rh', dashboardUuid],
    queryFn: () => dashboardRhService.getDashboard(dashboardUuid!),
    enabled: !!dashboardUuid,
  });

  const isLoading = isLoadingFolder || isLoadingDashboard;
  const dashboard = dashboardData?.data;
  const { evolutions, lastMonth, formatCurrency, formatPercent, filteredIndicators, summary } =
    useDashboardCalculations(dashboard, selectedEstablishment);

  // Extract unique establishments
  const establishments = (dashboard?.monthlyIndicators
    ? Object.values(
        dashboard.monthlyIndicators.reduce((acc, m) => {
          if (m.establishmentCode && m.establishmentName) {
            acc[m.establishmentCode] = { code: m.establishmentCode, name: m.establishmentName };
          }
          return acc;
        }, {} as Record<string, { code: string; name: string }>)
      ).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : []);

  if (isLoading) {
    return (
      <div className="mx-auto space-y-6 p-6">
        <LoadingSkeleton className="h-8 w-64" />
        <LoadingSkeleton className="h-12 w-full" />
        <LoadingSkeleton className="h-96" />
      </div>
    );
  }

  if (dashboardError || !dashboardData || !dashboard || !lastMonth || !summary) {
    return (
      <div className="mx-auto space-y-6 p-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/folders/${folderUuid}`)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              {t('dashboardRh.page.title', 'Tableau de bord')}
            </h1>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-error-100 mb-4 rounded-full p-3">
            <AlertCircle className="text-error-600 h-6 w-6" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('common.error', 'Erreur')}
          </h3>
          <p className="text-sm text-neutral-600">
            {t(
              'dashboardRh.page.error',
              'Impossible de charger le tableau de bord'
            )}
          </p>
        </div>
      </div>
    );
  }

  const { previousPeriodSummary } = dashboard;

  return (
    <div className="mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/folders/${folderUuid}`)}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-300 text-neutral-500 transition-colors hover:bg-neutral-50 hover:text-neutral-700"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-900">
              {t('dashboardRh.page.title', 'Tableau de bord')}
            </h1>
            <p className="text-sm text-neutral-600">
              {dashboard.folderName} - {dashboard.startPeriod} à{' '}
              {dashboard.endPeriod}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Establishment Filter */}
          <div className="relative">
            <select
              value={selectedEstablishment || ''}
              onChange={(e) => setSelectedEstablishment(e.target.value || null)}
              className="h-9 rounded-lg border border-neutral-300 bg-white px-3 py-1 text-sm text-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">{t('dashboardRh.filter.all', 'Tous les établissements')}</option>
              {establishments.map((e) => (
                <option key={e.code} value={e.code as string}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setIsShareModalOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Share2 className="h-4 w-4" />
            {t('dashboardRh.share.title', 'Partager')}
          </button>
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Download className="h-4 w-4" />
            {t('dashboardRh.export.title', 'Exporter')}
          </button>
        </div>
      </div>

      {/* Key Indicators */}
      <DashboardIndicators
        summary={summary}
        previousPeriodSummary={previousPeriodSummary}
        lastMonth={lastMonth}
        evolutions={evolutions}
        formatCurrency={formatCurrency}
        formatPercent={formatPercent}
      />

      {/* Charts */}
      <DashboardCharts
        monthlyIndicators={filteredIndicators}
        lastMonth={lastMonth}
        formatCurrency={formatCurrency}
        formatPercent={formatPercent}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        dashboardUuid={dashboardUuid!}
        folderName={dashboard.folderName}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        dashboardUuid={dashboardUuid!}
        folderName={dashboard.folderName}
      />
    </div>
  );
}
